import spartaEventsUtility from "utilities/global/sparta-events-utility/2.4.2/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.2.4/js/sparta-global-functions-utility";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.2/js/sparta-store-utility";
import Handlebars from "utilities/vendor/handlebars/4.7.9/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.9/js/handlebars";
import spartaTransportUtility from "utilities/global/sparta-transport-utility/4.2.1/js/sparta-transport-utility";
/* global spaParams spartaEventsUtility */

const CURRENT_TIME = Date.now();
const PERSIST_KEY = 'spartaTestExperiences';
const QS_PREFIX_KEY = 'experience';
const QS_DEBUG_KEY = 'experience-debug';
const QS_DEBUG_RESET_KEY = 'reset';
const EXPERIENCE_SCOPES = ['global', 'site', 'page'];
const SPARTA_EVENT_NAME = 'sparta-experiences-ready';
const DEFAULT_TESTS = {
  global: {},
  site: {},
  page: {},
};
const MANIFEST_TIMEOUT = 500;

/**
 * Get the tests from the manifest.
 * @returns {promise} Promise resolves with tests from manifest if it exists.
 *   Otherwise resolves with default tests (none).
 */
function getTestsFromManifest() {
  return new Promise((resolve) => {
    if (spaParams.experiencesPath) {
      spartaTransportUtility.subscribe({
        url: spaParams.experiencesPath,
        success: resolve,
        error: () => {
          resolve(DEFAULT_TESTS);
        },
        timeout: MANIFEST_TIMEOUT,
      });
    } else {
      resolve(DEFAULT_TESTS);
    }
  });
}

/**
 * Loops through each test in the nested object and calls the callback passed on it.
 * @param {object} tests - The tests to loop through.
 * @param {function} callback - The function to call on each test.
 * @returns {void}
 */
function forAllTests(tests, callback) {
  _.forEach(tests, (testsInScope, scope) => {
    _.forEach(testsInScope, (test, testName) => {
      callback({
        testsInScope,
        scope,
        test,
        testName,
      });
    });
  });
}

/**
 * Tests the start/end date/time of the test against current date/time.
 * @param {object} test - The test to compare start/end.
 * @returns {boolean} Indicates whether the test is in range.
 */
function isTestInTimeRange(test) {
  return (
    CURRENT_TIME >= new Date(test.runDates.start).getTime()
    && CURRENT_TIME <= new Date(test.runDates.end).getTime()
  );
}

/**
 * Filters the tests.
 * @param {object} allTests - The tests to filter.
 * @returns {object} The filtered tests.
 */
function filterTests(allTests) {
  const filteredTests = _.cloneDeep(allTests);

  forAllTests(filteredTests, ({
    testsInScope,
    scope,
    test,
    testName,
  }) => {
    const scopedTests = testsInScope;

    if (
      !isTestInTimeRange(test)
      || (scope === 'site' && test.name !== spaParams.siteName)
      || (scope === 'page' && !window.location.pathname.includes(test.path))
    ) {
      delete scopedTests[testName];
    }

    return scopedTests;
  });

  if (spartaGlobalFunctions.getUrlParam(QS_DEBUG_KEY)) {
    console.warn('Applicable experiences: ', filteredTests);
  }

  return filteredTests;
}

/**
 * Takes a set and picks one at random from it.
 * @param {object} spec - The spec to weight against.
 * @returns {string} The random key.
 */
function weightedRand(spec) {
  const table = [];
  let j;

  _.forEach(spec, (value, key) => {
    for (j = 0; j < value * 100; j += 1) {
      table.push(key);
    }
  });

  return table[Math.floor(Math.random() * table.length)];
}

/**
 * Takes the set of experiences and picks one at random.
 * @param {object} experiences - The experiences to pick at random from.
 * @returns {string} The test experience key (letter).
 */
function getRandomWeightedExperience(experiences) {
  return weightedRand(_.reduce(experiences, (total, experience) => {
    const newTotal = total;

    newTotal[experience.name] = experience.weight;

    return newTotal;
  }, {}));
}

/**
 * Uses sparta-store-utility to persist tests.
 * @param {object} tests - The tests to persist.
 * @returns {void}
 */
function persistTests(tests) {
  if (spartaGlobalFunctions.getUrlParam(QS_DEBUG_KEY)) {
    console.warn('All chosen experiences: ', tests);
  }

  spartaStoreUtility.persist(PERSIST_KEY, tests);
}

/**
 * Gets the persisted tests from sparta-store-utility.
 * @returns {object} The persisted tests if they exist.
 */
function getPersistedTests() {
  if (spartaGlobalFunctions.getUrlParam(QS_DEBUG_KEY) === QS_DEBUG_RESET_KEY) {
    spartaStoreUtility.removePersisted(PERSIST_KEY);
  }

  return spartaStoreUtility.getPersisted(PERSIST_KEY) || DEFAULT_TESTS;
}

/**
 * Goes through the tests and chooses experiences.
 * @param {object} allTests - The tests to choose experiences for.
 * @returns {object} The tests with chosen experiences.
 */
function chooseExperiencesForTests(allTests) {
  const persistedTests = getPersistedTests();

  forAllTests(allTests, ({
    scope,
    test,
    testName,
  }) => {
    const currentTest = test;

    // Avoid a console error if there are no stored tests.
    if (!persistedTests || !persistedTests[scope]) {
      return null;
    }

    const experienceFromQs = spartaGlobalFunctions
      .getUrlParam(`${QS_PREFIX_KEY}.${scope}.${testName}`);
    const persistedTest = persistedTests[scope][testName];

    if (experienceFromQs !== null && _.find(test.experiences, { name: experienceFromQs })) {
      currentTest.experience = experienceFromQs;
    } else if (persistedTest && _.find(test.experiences, { name: persistedTest.experience })) {
      currentTest.experience = persistedTest.experience;
    } else {
      currentTest.experience = getRandomWeightedExperience(test.experiences);
    }

    return currentTest;
  });

  persistTests(allTests);

  return allTests;
}

/**
 * Provides methods for ABn campaigns and tests.
 */
class SpartaExperienceUtility {
  /**
   * Sets up a new instance.
   */
  constructor() {
    // Switch to turn it off. Only works with pages for now; no widget support yet.
    if (
      !window.spaParams
      || !spaParams.siteParams.spartaExperiences
      || spaParams.pageParams.disableSpartaExperiences === true
    ) {
      return;
    }

    this.loadExperiences();
  }

  /**
   * Load the experience json, filter results, and publish a sparta event.
   * @returns {undefined}
   */
  async loadExperiences() {
    const tests = await getTestsFromManifest();
    this.chosenTests = await chooseExperiencesForTests(tests);
    this.filteredTests = await filterTests(this.chosenTests);

    this.setupFluentAPI();

    spartaEventsUtility.pub(SPARTA_EVENT_NAME, {
      filteredTests: this.filteredTests,
      chosenTests: this.chosenTests,
    });
  }

  /**
   * Sets up a fluent API that allows polling experience values at each scope (global, site, page).
   *
   * API:
   * <global|site|page>.is(<test name>, <experience name>)
   *
   * @example
   * this.Experience.global.is('globalTestA', 'c').then(() => {
   *   console.log('globalTestA is c');
   * }, () => {
   *   console.log('globalTestA isn\'t c');
   * });
   *
   * @param {string} testName - The name of the test to check the experience against.
   * @param {string} experienceName - The name of the experience to check.
   * @returns {promise} Resolves if the user should see the experience for the given test.
   *   Otherwise, rejects.
   */
  setupFluentAPI() {
    EXPERIENCE_SCOPES.forEach((scope) => {
      const self = this;

      this[scope] = {
        /**
         * Check if experience in a test is current one.
         *
         * @param {string} testName - The test name to check experience against.
         * @param {string} experienceName - The experience to check against `testName`.
         *
         * @example
         * this.Experience.global.is('globalTestA', 'c').then(() => {
         *   console.log('globalTestA is c');
         * }, () => {
         *   console.log('globalTestA isn\'t c');
         * });
         *
         * @returns {promise} Resolves promise if it's the current experience or rejects if not.
         */
        is: (testName, experienceName) => {
          const filteredPromise = self.filteredTests.then((filteredTests) => {
            const result = (
              filteredTests[scope][testName]
              && filteredTests[scope][testName].experience === experienceName
            ) ? Promise.resolve() : Promise.reject();

            return result;
          });

          return filteredPromise;
        },

        /**
         * Gets a test by name.
         *
         * @example
         * this.Experience.site.get('someTestName', selectedExperience => {
         *    this.renderPartial(this.jcr.experiences[selectedExperience]);
         * });
         *
         * @param {string} testName - The test name to look for.
         * @returns {promise} Promise that resolves with current experience.
         */
        get: (testName) => {
          const filteredPromise = self.filteredTests.then((filteredTests) => {
            const result = filteredTests[scope][testName]
              ? Promise.resolve(filteredTests[scope][testName].experience)
              : Promise.reject();

            return result;
          });

          return filteredPromise;
        },
      };
    });
  }
}

export default new SpartaExperienceUtility();
